"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { isTypingTarget } from "@/lib/keyboard";
import { openNavSearch } from "@/lib/navigation/nav-search-open";

/**
 * Type-to-search over the side nav. The first printable key pressed while the
 * pointer is over the nav, or while focus is inside it, opens the ⌘K dialog
 * seeded with that character and ranking the shown menu's pages first.
 * Someone typing at a menu is naming a destination, and the dialog searches
 * every destination, including the ones that menu never lists, so a per-menu
 * filter would be both narrower and a second search to learn.
 *
 * Scoped to the nav on purpose: the panel stays open for the whole visit, so
 * a page-wide listener would steal keystrokes from the page. Keys pressed in
 * a field, or with a modifier, pass through. Returns the props the nav
 * element takes (`ref`, `onMouseEnter`, `onMouseLeave`).
 *
 * @param {string} menuLabel The menu whose pages lead the results.
 */
export function useNavTypeToSearch(menuLabel) {
  const navRef = useRef(null);
  const hoveringRef = useRef(false);
  // Read through a ref so the listener is attached once, not on every render.
  const labelRef = useRef(menuLabel);
  useLayoutEffect(() => {
    labelRef.current = menuLabel;
  }, [menuLabel]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1 || !/\S/.test(event.key)) return;
      const nav = navRef.current;
      const atNav =
        hoveringRef.current || Boolean(nav?.contains(document.activeElement));
      if (!atNav) return;

      event.preventDefault();
      openNavSearch({
        query: event.key,
        pagesFirst: true,
        menuLabel: labelRef.current,
      });
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const onMouseEnter = useCallback(() => {
    hoveringRef.current = true;
  }, []);
  const onMouseLeave = useCallback(() => {
    hoveringRef.current = false;
  }, []);

  return { ref: navRef, onMouseEnter, onMouseLeave };
}
